import { useEffect, useState } from 'react';
import ComparativeCard from '@/components/Modals/PriceComparativeChartModal/ComparativeCard';
import PriceLineChart from '@/components/Modals/PriceComparativeChartModal/PriceLineChart';
import RefineFilter from '@/components/Modals/PriceComparativeChartModal/RefineFilter';
import { useLoading } from '@/contexts/LoadingContext';
import { ILocationList } from '@/pages/preco-regiao';
import {
  IBrandsPricingComparative,
  IItemBrandPricingComparative,
  IPricingComparative,
  IChartDateFilter,
  ChartDateFilterLabels,
} from '@/types/api/pricingsComparativeRegion';
import {
  IGetComparativeDateProps,
  dateFilters,
  generateComparativeList,
  getComparativeData,
} from '@/utils/components/priceComparativeChartModal';
import { Close } from '@mui/icons-material';
import { Box, Fade, IconButton, Modal, Typography } from '@mui/material';

interface IPriceComparativeChartModalProps {
  isOpen: boolean;
  handleClose: () => void;
  onChangeLocation: (location: string) => void;
  initialItem: Nullable<IPricingComparative>;
  initialBrand: Nullable<IBrandsPricingComparative>;
  initialLocation: ILocationList;
  items: IPricingComparative[];
  locations: ILocationList[];
}

const PriceComparativeChartModal = ({
  handleClose,
  isOpen,
  initialBrand,
  initialItem,
  initialLocation,
  onChangeLocation,
  locations: propLocations,
  items: propItems,
}: IPriceComparativeChartModalProps) => {
  const { setIsLoading } = useLoading();
  const [items, setItems] = useState(
    generateComparativeList(propItems, initialItem)
  );
  const [locations, setLocations] = useState(
    generateComparativeList(propLocations, initialLocation)
  );
  const [brands, setBrands] = useState(
    generateComparativeList(initialItem?.brands, initialBrand)
  );
  const [currentFilter, setCurrentFilter] = useState(
    dateFilters.find(
      filter => filter.name === ChartDateFilterLabels.ThirtyDays
    ) as IChartDateFilter
  );
  const [comparativeData, setComparativeData] =
    useState<IItemBrandPricingComparative>();

  const handleChangeDateFilter = async (value: number) => {
    const success = await getComparative({ time_range: value });

    if (!success) return;

    setCurrentFilter(
      dateFilters.find(
        filter => filter.valueInSeconds === value
      ) as IChartDateFilter
    );
  };

  const handleChangeItem = async (itemId: number) => {
    const success = await getComparative({ item_id: itemId });

    if (!success) return;

    setItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          setBrands(generateComparativeList(item.brands, null));
        }
        return { ...item, selected: item.id === itemId };
      })
    );
  };

  const handleChangeBrand = async (brandId: number | null) => {
    const success = await getComparative({ brand_id: brandId });

    if (!success) return;

    setBrands(prevBrands =>
      prevBrands.map(brand => ({ ...brand, selected: brand.id === brandId }))
    );
  };

  const handleChangeLocation = async (locationId: number) => {
    const success = await getComparative({ location_id: locationId });

    if (!success) return;

    const currentLocation = locations.find(
      location => location.id === locationId
    ) as ILocationList;

    onChangeLocation(currentLocation.name);

    setLocations(prevLocations =>
      prevLocations.map(location => ({
        ...location,
        selected: location.id === locationId,
      }))
    );
  };

  async function getComparative(props: IGetComparativeDateProps = {}) {
    const stateParams: IGetComparativeDateProps = {
      brand_id: brands.find(brand => brand.selected)?.id,
      item_id: items.find(item => item.selected)?.id,
      location_id: locations.find(location => location.selected)?.id,
      time_range: currentFilter.valueInSeconds,
    };

    setIsLoading(true);
    const comparative = await getComparativeData({
      ...stateParams,
      ...props,
    }).finally(() => setIsLoading(false));

    if (comparative) setComparativeData(comparative);

    return Boolean(comparative);
  }

  useEffect(() => {
    (async () => await getComparative())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal open={isOpen} onClose={handleClose} closeAfterTransition>
      <Fade in={isOpen} exit={!isOpen} timeout={300}>
        <Box
          sx={theme => ({
            paddingTop: '64px',
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'background.paper',
            [theme.breakpoints.up('md')]: {
              width: '843px',
              height: '619px',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              borderRadius: '12px',
            },
          })}
        >
          <Box
            component="header"
            sx={{
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingY: 2,
              boxShadow: '0px 1.44353px 5.77413px rgba(0, 0, 0, 0.1)',
              position: 'absolute',
              top: 0,
              zIndex: 1,
              width: '100%',
            }}
          >
            <Typography variant="h6" component="h1">
              Histórico de preço
            </Typography>

            <Box sx={{ position: 'absolute', right: 14, top: 16 }}>
              <IconButton
                size="small"
                color="inherit"
                onClick={handleClose}
                aria-label="button-close-modal"
              >
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Box
            component="main"
            sx={{
              p: 2,
              width: '100%',
              height: '100% ',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Box component="section" sx={{ width: '100%' }}>
              <RefineFilter
                onChangeDateFilter={handleChangeDateFilter}
                onChangeItemFilter={handleChangeItem}
                onChangeBrandFilter={handleChangeBrand}
                onChangeLocationFilter={handleChangeLocation}
                locations={locations}
                items={items}
                brands={brands}
              />
            </Box>

            <Box
              component="section"
              sx={theme => ({
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                [theme.breakpoints.up('md')]: {
                  flexDirection: 'row',
                  gap: 2,
                },
              })}
            >
              <Box
                sx={theme => ({
                  width: '100%',
                  minHeight: '300px',
                  height: 'calc(100% - 330px)',
                  [theme.breakpoints.up('md')]: {
                    width: '620px',
                    height: '100%',
                  },
                })}
              >
                <PriceLineChart comparative={comparativeData} />
              </Box>
              <Box
                sx={theme => ({
                  pb: 2,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  [theme.breakpoints.up('md')]: {
                    justifyContent: 'space-around',
                    height: 'calc(100% - 70px)',
                    width: 'calc(100% - 650px)',
                    pb: 0,
                  },
                })}
              >
                <ComparativeCard.LowerPrice comparative={comparativeData} />
                <ComparativeCard.AveragePrice comparative={comparativeData} />
                <ComparativeCard.Variation
                  comparative={comparativeData}
                  filter={currentFilter.name}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default PriceComparativeChartModal;
export type { IPriceComparativeChartModalProps };
